import { useCallback, useEffect, useMemo, useState } from "react";
import { STORAGE_KEYS } from "../config/newsConfig";
import {
  getSourceKey,
  isSameSource,
  normalizeSourceName,
} from "../utils/sourceModel";
import { readStoredJson, writeStoredJson } from "../utils/storage";
import { mergeUniqueNewsByPublished } from "../utils/newsModel";
import {
  getUnreadBoundaryTimestamp,
  getUnreadEligiblePublishedTimestamp,
} from "../utils/unreadStorage";

const hasOwn = (object, key) => {
  return Object.prototype.hasOwnProperty.call(object || {}, key);
};

const getSafeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const getSafeTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const useUnreadCounts = ({
  allNews = [],
  focusNewsBySource = {},
  focusSource = null,
  activeSources = null,
  availableSources = [],
  sourceTotalCounts = {},
  unreadFallbackCounts = {},
  unreadCountSince = "",
} = {}) => {
  const [readLinks, setReadLinks] = useState(() => {
    return new Set(readStoredJson(STORAGE_KEYS.read, []));
  });

  const [readSources, setReadSources] = useState(() => {
    return readStoredJson(STORAGE_KEYS.readSources, {});
  });

  /*
  |--------------------------------------------------------------------------
  | Individually-read news that belongs to the unread window
  |--------------------------------------------------------------------------
  |
  | General readLinks is still used for the shared Home/Focus read state.
  | This second map is intentionally stricter: it stores publication metadata
  | only for news that is allowed to reduce the 24-hour unread total.
  |
  */
  const [unreadReadItems, setUnreadReadItems] = useState(() => {
    return readStoredJson(STORAGE_KEYS.unreadReadItems, {});
  });

  /*
  |--------------------------------------------------------------------------
  | News explicitly committed from the fresh-news capsule
  |--------------------------------------------------------------------------
  |
  | Some crawled rows can be published before a Mark All Read moment but only
  | enter Home later through the fresh-news capsule. Their publication time is
  | therefore not a reliable indicator of when they entered the unread model.
  |
  | This map stores the actual commit time for those rows. A committed row can
  | then reduce Unread after it becomes visible, even when its publication time
  | is older than the current Mark All Read baseline.
  |
  */
  const [committedUnreadItems, setCommittedUnreadItems] = useState(() => {
    return readStoredJson(
      STORAGE_KEYS.unreadCommittedItems,
      {}
    );
  });

  /*
  |--------------------------------------------------------------------------
  | Mark-All-Read baselines
  |--------------------------------------------------------------------------
  |
  | A baseline stores the exact source count at the moment Mark All Read was
  | used. Later unread is the source's new total minus that baseline, minus any
  | individually-read news that entered the unread model after that moment.
  |
  */
  const [readAllBaselines, setReadAllBaselines] = useState(() => {
    return readStoredJson(
      STORAGE_KEYS.unreadReadAllBaselines,
      {}
    );
  });

  /*
  |--------------------------------------------------------------------------
  | Aggregate read-backfill suppression for Large Backlog snapshots
  |--------------------------------------------------------------------------
  |
  | A reconnect can discover thousands of inserted rows without downloading
  | them. Rows that fall inside a Home range the user already read are counted
  | by the API and suppressed here by source. Session keys prevent the same
  | fixed insert window from being subtracted twice across retries/reloads.
  |
  */
  const [suppressedBackfillSessions, setSuppressedBackfillSessions] =
    useState(() => {
      return readStoredJson(
        STORAGE_KEYS.unreadSuppressedBackfillSessions,
        {}
      );
    });

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.read, [...readLinks]);
  }, [readLinks]);

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.readSources, readSources);
  }, [readSources]);

  useEffect(() => {
    writeStoredJson(
      STORAGE_KEYS.unreadReadItems,
      unreadReadItems
    );
  }, [unreadReadItems]);

  useEffect(() => {
    writeStoredJson(
      STORAGE_KEYS.unreadCommittedItems,
      committedUnreadItems
    );
  }, [committedUnreadItems]);

  useEffect(() => {
    writeStoredJson(
      STORAGE_KEYS.unreadReadAllBaselines,
      readAllBaselines
    );
  }, [readAllBaselines]);

  useEffect(() => {
    writeStoredJson(
      STORAGE_KEYS.unreadSuppressedBackfillSessions,
      suppressedBackfillSessions
    );
  }, [suppressedBackfillSessions]);

  const allKnownNews = useMemo(() => {
    return mergeUniqueNewsByPublished(
      allNews,
      ...Object.values(focusNewsBySource)
    );
  }, [allNews, focusNewsBySource]);

  const knownNewsByLink = useMemo(() => {
    const map = new Map();

    allKnownNews.forEach((item) => {
      if (item?.link) {
        map.set(item.link, item);
      }
    });

    return map;
  }, [allKnownNews]);

  /*
  |--------------------------------------------------------------------------
  | Register rows when the fresh-news capsule is accepted
  |--------------------------------------------------------------------------
  |
  | Polling does not make a row unread. The row becomes unread only when the
  | user taps the capsule and the row is inserted into Home. The commit time is
  | persisted per link so reading that row can always reduce the same count that
  | was added by the capsule click.
  |
  */
  const registerCommittedUnreadItems = useCallback(
    (items = [], alreadyReadItems = []) => {
      const committedAt = new Date().toISOString();
      const records = {};
      const forcedReadLinks = new Set(
        alreadyReadItems
          .map((item) => item?.link)
          .filter(Boolean)
      );

      items.forEach((item) => {
        const link = item?.link;
        const source = normalizeSourceName(item?.source);
        const publishedTimestamp =
          getUnreadEligiblePublishedTimestamp(
            item,
            unreadCountSince
          );

        if (!link || !source || !publishedTimestamp) {
          return;
        }

        records[link] = {
          source,
          publishedAt: new Date(
            publishedTimestamp
          ).toISOString(),
          committedAt,
        };
      });

      if (Object.keys(records).length === 0) return;

      setCommittedUnreadItems((previous) => {
        return {
          ...previous,
          ...records,
        };
      });

      /*
      | A row may already have been read in Focus before it was accepted into
      | Home. Upgrade that existing read record immediately so the newly added
      | unread total is reduced in the same state transition.
      */
      setUnreadReadItems((previous) => {
        const next = { ...previous };
        let changed = false;

        Object.entries(records).forEach(([link, record]) => {
          if (
            !readLinks.has(link) &&
            !forcedReadLinks.has(link)
          ) {
            return;
          }

          const previousRecord = previous[link] || {};

          next[link] = {
            ...previousRecord,
            source: record.source,
            publishedAt: record.publishedAt,
            eligibleAt: record.committedAt,
            readAt:
              previousRecord.readAt ||
              committedAt,
          };

          changed = true;
        });

        return changed ? next : previous;
      });
    },
    [readLinks, unreadCountSince]
  );

  /*
  |--------------------------------------------------------------------------
  | Hydrate unread metadata for already-read links
  |--------------------------------------------------------------------------
  |
  | Older app versions stored only the link/source. When one of those links is
  | encountered again inside the fixed unread window, attach its publication
  | metadata so refreshes keep the correct count without losing read history.
  |
  */
  useEffect(() => {
    if (!unreadCountSince || readLinks.size === 0) return;

    setUnreadReadItems((previous) => {
      let changed = false;
      const next = { ...previous };

      allKnownNews.forEach((item) => {
        const link = item?.link;

        if (!link || !readLinks.has(link)) {
          return;
        }

        const publishedTimestamp =
          getUnreadEligiblePublishedTimestamp(
            item,
            unreadCountSince
          );

        const source = normalizeSourceName(item?.source);

        if (!publishedTimestamp || !source) return;

        const committedRecord = committedUnreadItems[link];
        const eligibleAt =
          committedRecord?.committedAt ||
          new Date(publishedTimestamp).toISOString();
        const previousRecord = previous[link];

        if (
          previousRecord &&
          previousRecord.source === source &&
          previousRecord.publishedAt ===
            new Date(publishedTimestamp).toISOString() &&
          previousRecord.eligibleAt === eligibleAt
        ) {
          return;
        }

        next[link] = {
          ...previousRecord,
          source,
          publishedAt: new Date(
            publishedTimestamp
          ).toISOString(),
          eligibleAt,
          readAt:
            previousRecord?.readAt ||
            new Date().toISOString(),
        };

        changed = true;
      });

      return changed ? next : previous;
    });
  }, [
    allKnownNews,
    committedUnreadItems,
    readLinks,
    unreadCountSince,
  ]);

  const markItemsAsRead = useCallback(
    (items = [], options = {}) => {
      const trackUnread = options.trackUnread !== false;
      const readableItems = items.filter((item) => {
        return (
          item &&
          typeof item === "object" &&
          typeof item.link === "string" &&
          item.link
        );
      });

      if (readableItems.length === 0) return;

      setReadSources((previous) => {
        const next = { ...previous };
        let changed = false;

        readableItems.forEach((item) => {
          const source = normalizeSourceName(item?.source);

          if (!source || next[item.link] === source) return;

          next[item.link] = source;
          changed = true;
        });

        return changed ? next : previous;
      });

      setReadLinks((previous) => {
        const next = new Set(previous);
        const previousSize = next.size;

        readableItems.forEach((item) => {
          next.add(item.link);
        });

        return next.size === previousSize ? previous : next;
      });

      /*
      | Backfill rows can be marked visually read before they enter the unread
      | model. In that case, preserve readLinks/readSources now and let the
      | capsule commit create the matching unread deduction later.
      */
      if (!trackUnread) return;

      setUnreadReadItems((previous) => {
        const next = { ...previous };
        const readAt = new Date().toISOString();
        let changed = false;

        readableItems.forEach((item) => {
          const source = normalizeSourceName(item?.source);
          const publishedTimestamp =
            getUnreadEligiblePublishedTimestamp(
              item,
              unreadCountSince
            );

          if (!source || !publishedTimestamp) return;

          const publishedAt = new Date(
            publishedTimestamp
          ).toISOString();
          const committedAt =
            committedUnreadItems[item.link]?.committedAt || "";
          const eligibleAt = committedAt || publishedAt;
          const previousRecord = previous[item.link];

          if (
            previousRecord?.source === source &&
            previousRecord?.publishedAt === publishedAt &&
            previousRecord?.eligibleAt === eligibleAt
          ) {
            return;
          }

          next[item.link] = {
            ...previousRecord,
            source,
            publishedAt,
            eligibleAt,
            readAt: previousRecord?.readAt || readAt,
          };

          changed = true;
        });

        return changed ? next : previous;
      });
    },
    [committedUnreadItems, unreadCountSince]
  );

  const markAsRead = useCallback(
    (newsOrLink, fallbackSource = "") => {
      const suppliedNews =
        newsOrLink && typeof newsOrLink === "object"
          ? newsOrLink
          : null;

      const link = suppliedNews?.link || newsOrLink;

      if (!link || typeof link !== "string") return;

      const knownNews = suppliedNews || knownNewsByLink.get(link);

      if (knownNews) {
        markItemsAsRead([knownNews]);
        return;
      }

      const source = normalizeSourceName(fallbackSource);

      if (source) {
        setReadSources((previous) => {
          if (previous[link] === source) return previous;

          return {
            ...previous,
            [link]: source,
          };
        });
      }

      setReadLinks((previous) => {
        if (previous.has(link)) return previous;

        const next = new Set(previous);
        next.add(link);
        return next;
      });
    },
    [knownNewsByLink, markItemsAsRead]
  );

  const registerSuppressedBackfillCounts = useCallback(
    (sessionKey, countsBySource = {}) => {
      const normalizedSessionKey = String(sessionKey || "").trim();

      if (!normalizedSessionKey) return;

      const normalizedCounts = Object.entries(countsBySource).reduce(
        (result, [source, value]) => {
          const normalizedSource = normalizeSourceName(source);
          const count = Math.floor(getSafeCount(value));

          if (normalizedSource && count > 0) {
            result[normalizedSource] = count;
          }

          return result;
        },
        {}
      );

      if (Object.keys(normalizedCounts).length === 0) return;

      setSuppressedBackfillSessions((previous) => {
        if (hasOwn(previous, normalizedSessionKey)) {
          return previous;
        }

        return {
          ...previous,
          [normalizedSessionKey]: normalizedCounts,
        };
      });
    },
    []
  );

  const suppressedBackfillCountsByKey = useMemo(() => {
    return Object.values(suppressedBackfillSessions).reduce(
      (result, sessionCounts) => {
        Object.entries(sessionCounts || {}).forEach(([source, value]) => {
          const sourceKey = getSourceKey(source);

          if (!sourceKey) return;

          result[sourceKey] =
            (result[sourceKey] || 0) + getSafeCount(value);
        });

        return result;
      },
      {}
    );
  }, [suppressedBackfillSessions]);

  const sourceCountsByKey = useMemo(() => {
    return Object.entries(sourceTotalCounts).reduce(
      (counts, [source, value]) => {
        const sourceKey = getSourceKey(source);

        if (!sourceKey) return counts;

        counts[sourceKey] =
          (counts[sourceKey] || 0) + getSafeCount(value);

        return counts;
      },
      {}
    );
  }, [sourceTotalCounts]);

  useEffect(() => {
    setReadAllBaselines((previous) => {
      let changed = false;
      const next = { ...previous };

      Object.entries(previous).forEach(([sourceKey, baseline]) => {
        if (
          baseline?.exact !== false ||
          !hasOwn(sourceCountsByKey, sourceKey)
        ) {
          return;
        }

        next[sourceKey] = {
          ...baseline,
          count: getSafeCount(sourceCountsByKey[sourceKey]),
          at: new Date().toISOString(),
          exact: true,
        };

        changed = true;
      });

      return changed ? next : previous;
    });
  }, [sourceCountsByKey]);

  const fallbackCountsByKey = useMemo(() => {
    return Object.entries(unreadFallbackCounts).reduce(
      (counts, [source, value]) => {
        const sourceKey = getSourceKey(source);

        if (!sourceKey) return counts;

        counts[sourceKey] =
          (counts[sourceKey] || 0) + getSafeCount(value);

        return counts;
      },
      {}
    );
  }, [unreadFallbackCounts]);

  const getCurrentSourceTotal = useCallback(
    (source) => {
      const sourceKey = getSourceKey(source);

      if (!sourceKey) return 0;

      if (hasOwn(sourceCountsByKey, sourceKey)) {
        return getSafeCount(sourceCountsByKey[sourceKey]);
      }

      /*
      | If source_counts is temporarily unavailable, use only the initial
      | snapshot plus pending rows that the user explicitly accepted into Home.
      | Load More and unaccepted polling rows never enter this fallback.
      */
      return getSafeCount(fallbackCountsByKey[sourceKey]);
    },
    [fallbackCountsByKey, sourceCountsByKey]
  );

  const unreadCounts = useMemo(() => {
    const boundaryTimestamp = getUnreadBoundaryTimestamp(
      unreadCountSince
    );

    const individuallyReadCounts = {};

    Object.values(unreadReadItems).forEach((record) => {
      const sourceKey = getSourceKey(record?.source);
      const publishedTimestamp = getSafeTimestamp(
        record?.publishedAt
      );
      const eligibleTimestamp = getSafeTimestamp(
        record?.eligibleAt ||
          record?.committedAt ||
          record?.publishedAt
      );

      if (
        !sourceKey ||
        !boundaryTimestamp ||
        publishedTimestamp < boundaryTimestamp ||
        !eligibleTimestamp
      ) {
        return;
      }

      const baselineAt = getSafeTimestamp(
        readAllBaselines[sourceKey]?.at
      );

      /*
      | Mark All Read covers rows that had already entered the unread model at
      | that moment. For normal initial rows, eligibleAt equals publishedAt. For
      | capsule rows, eligibleAt is the later capsule-commit time. This keeps a
      | delayed crawler row deductible after it is accepted and then viewed.
      */
      if (baselineAt && eligibleTimestamp <= baselineAt) {
        return;
      }

      individuallyReadCounts[sourceKey] =
        (individuallyReadCounts[sourceKey] || 0) + 1;
    });

    const allSourcesForCounts = new Set([
      ...availableSources.map(getSourceKey),
      ...Object.keys(sourceCountsByKey),
      ...Object.keys(fallbackCountsByKey),
      ...Object.keys(readAllBaselines),
      ...Object.values(unreadReadItems)
        .map((record) => getSourceKey(record?.source))
        .filter(Boolean),
    ]);

    const counts = {};

    allSourcesForCounts.forEach((source) => {
      if (!source) return;

      const total = getCurrentSourceTotal(source);
      const baselineCount = getSafeCount(
        readAllBaselines[source]?.count
      );
      const individuallyRead =
        individuallyReadCounts[source] || 0;
      const suppressedBackfill =
        suppressedBackfillCountsByKey[source] || 0;

      counts[source] = Math.max(
        0,
        total - baselineCount - individuallyRead - suppressedBackfill
      );
    });

    return counts;
  }, [
    availableSources,
    getCurrentSourceTotal,
    fallbackCountsByKey,
    readAllBaselines,
    sourceCountsByKey,
    suppressedBackfillCountsByKey,
    unreadCountSince,
    unreadReadItems,
  ]);

  const markLoadedItemsRead = useCallback((items) => {
    const readableItems = items.filter(
      (item) => item?.link
    );

    if (readableItems.length === 0) return;

    setReadSources((previous) => {
      const next = { ...previous };
      let changed = false;

      readableItems.forEach((item) => {
        const source = normalizeSourceName(item?.source);

        if (!source || next[item.link] === source) return;

        next[item.link] = source;
        changed = true;
      });

      return changed ? next : previous;
    });

    setReadLinks((previous) => {
      const next = new Set(previous);
      const previousSize = next.size;

      readableItems.forEach((item) => {
        next.add(item.link);
      });

      return next.size === previousSize ? previous : next;
    });
  }, []);

  const setSourcesReadBaseline = useCallback(
    (sources) => {
      const sourceKeys = Array.from(
        new Set(
          sources
            .map(getSourceKey)
            .filter(Boolean)
        )
      );

      if (sourceKeys.length === 0) return;

      const markedAt = new Date().toISOString();

      setReadAllBaselines((previous) => {
        const next = { ...previous };

        sourceKeys.forEach((sourceKey) => {
          next[sourceKey] = {
            count: getCurrentSourceTotal(sourceKey),
            at: markedAt,
            exact: hasOwn(sourceCountsByKey, sourceKey),
          };
        });

        return next;
      });

      /*
      | Old individual records for these sources are now absorbed into the
      | baseline. Removing them prevents double subtraction and limits storage.
      */
      setUnreadReadItems((previous) => {
        const sourceSet = new Set(sourceKeys);
        const next = {};
        let changed = false;

        Object.entries(previous).forEach(([link, record]) => {
          const sourceKey = getSourceKey(record?.source);

          if (sourceSet.has(sourceKey)) {
            changed = true;
            return;
          }

          next[link] = record;
        });

        return changed ? next : previous;
      });

      /*
      | Every committed row currently known for these sources is now included
      | in the new baseline. Remove those old commit markers; later capsule
      | accepts will create fresh markers after this baseline.
      */
      setCommittedUnreadItems((previous) => {
        const sourceSet = new Set(sourceKeys);
        const next = {};
        let changed = false;

        Object.entries(previous).forEach(([link, record]) => {
          const sourceKey = getSourceKey(record?.source);

          if (sourceSet.has(sourceKey)) {
            changed = true;
            return;
          }

          next[link] = record;
        });

        return changed ? next : previous;
      });

      /*
      | The new exact baseline already absorbs every previously suppressed row
      | for these sources. Remove those source entries to avoid double subtracting
      | them from future totals.
      */
      setSuppressedBackfillSessions((previous) => {
        const sourceSet = new Set(sourceKeys);
        const next = {};
        let changed = false;

        Object.entries(previous).forEach(([sessionKey, sessionCounts]) => {
          const remainingCounts = {};

          Object.entries(sessionCounts || {}).forEach(([source, value]) => {
            if (sourceSet.has(getSourceKey(source))) {
              changed = true;
              return;
            }

            remainingCounts[source] = value;
          });

          if (Object.keys(remainingCounts).length > 0) {
            next[sessionKey] = remainingCounts;
          } else if (Object.keys(sessionCounts || {}).length > 0) {
            changed = true;
          }
        });

        return changed ? next : previous;
      });
    },
    [getCurrentSourceTotal, sourceCountsByKey]
  );

  const markAllReadFocusedSource = useCallback(() => {
    const normalizedFocusSource = normalizeSourceName(focusSource);

    if (!normalizedFocusSource) return;

    const itemsToMark = allKnownNews.filter((item) => {
      return isSameSource(item?.source, normalizedFocusSource);
    });

    markLoadedItemsRead(itemsToMark);
    setSourcesReadBaseline([normalizedFocusSource]);
  }, [
    allKnownNews,
    focusSource,
    markLoadedItemsRead,
    setSourcesReadBaseline,
  ]);

  const handleBottomMarkAllRead = useCallback(() => {
    if (focusSource) {
      markAllReadFocusedSource();
      return;
    }

    const sourcesToMark = activeSources || availableSources;
    const activeSourceSet = new Set(
      sourcesToMark.map(getSourceKey)
    );

    const itemsToMark = allKnownNews.filter((item) => {
      return activeSourceSet.has(getSourceKey(item?.source));
    });

    markLoadedItemsRead(itemsToMark);
    setSourcesReadBaseline(sourcesToMark);
  }, [
    activeSources,
    allKnownNews,
    availableSources,
    focusSource,
    markAllReadFocusedSource,
    markLoadedItemsRead,
    setSourcesReadBaseline,
  ]);

  const totalUnreadCount = useMemo(() => {
    if (!activeSources) return 0;

    return activeSources.reduce((sum, source) => {
      return sum + (unreadCounts[getSourceKey(source)] || 0);
    }, 0);
  }, [activeSources, unreadCounts]);

  const focusedUnreadCount = useMemo(() => {
    const sourceKey = getSourceKey(focusSource);
    return sourceKey ? unreadCounts[sourceKey] || 0 : 0;
  }, [focusSource, unreadCounts]);

  return {
    readLinks,
    readSources,
    unreadReadItems,
    committedUnreadItems,
    readAllBaselines,
    suppressedBackfillSessions,
    registerCommittedUnreadItems,
    registerSuppressedBackfillCounts,
    markAsRead,
    markItemsAsRead,
    markAllReadFocusedSource,
    handleBottomMarkAllRead,
    unreadCounts,
    totalUnreadCount,
    focusedUnreadCount,
  };
};
